import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, UserCog } from "lucide-react";

const empty = { username: "", email: "", password: "", password_confirm: "", name: "", branch_id: "" };

function passwordError(password) {
  const pwd = (password || "").trim();
  if (pwd.length < 8) return "Mínimo 8 caracteres";
  if (!/[A-Z]/.test(pwd)) return "Incluye al menos una mayúscula";
  if (!/[a-z]/.test(pwd)) return "Incluye al menos una minúscula";
  if (!/[0-9]/.test(pwd)) return "Incluye al menos un número";
  return null;
}

export default function AdminBranchAdmins() {
  const [admins, setAdmins] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [adminsRes, branchesRes] = await Promise.all([
      api.get("/admin/branch-admins"),
      api.get("/admin/branches"),
    ]);
    setAdmins(adminsRes.data || []);
    setBranches(branchesRes.data || []);
  };

  useEffect(() => {
    load().catch(() => toast.error("No se pudieron cargar los usuarios"));
  }, []);

  const save = async () => {
    if (!form.branch_id) {
      toast.error("Selecciona una sucursal");
      return;
    }
    const username = (form.username || "").trim().toLowerCase();
    if (!username) {
      toast.error("El usuario es obligatorio");
      return;
    }
    if (username.length < 3) {
      toast.error("El usuario debe tener al menos 3 caracteres");
      return;
    }
    if (!/^[a-z0-9._-]+$/.test(username)) {
      toast.error("Usuario inválido: solo minúsculas, números, punto, guion y guion bajo");
      return;
    }
    if (!(form.email || "").trim()) {
      toast.error("El correo es obligatorio");
      return;
    }
    const pwd = (form.password || "").trim();
    const pwdConfirm = (form.password_confirm || "").trim();
    const pwdErr = passwordError(pwd);
    if (pwdErr) {
      toast.error(pwdErr);
      return;
    }
    if (pwd !== pwdConfirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    try {
      await api.post("/admin/branch-admins", {
        username,
        email: form.email.trim(),
        password: pwd,
        name: form.name.trim(),
        branch_id: form.branch_id,
      });
      toast.success("Administrador creado");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((x) => x?.msg || x).join(", ") : "Error al crear";
      toast.error(msg);
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este administrador de sucursal?")) return;
    try {
      await api.delete(`/admin/branch-admins/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al eliminar");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">Maestro</div>
          <h1 className="font-display text-4xl text-white mt-1">Usuarios de sucursal</h1>
          <p className="text-white/60 text-sm mt-1">
            Crea administradores que solo ven y operan su sucursal asignada.
          </p>
        </div>
        <button type="button" onClick={() => setOpen(true)} className="btn-primary">
          <Plus size={16} /> Nuevo usuario
        </button>
      </div>

      <div className="glass rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-white/40 border-b border-white/10">
              <th className="p-4">Nombre</th>
              <th className="p-4">Usuario</th>
              <th className="p-4">Correo</th>
              <th className="p-4">Sucursal</th>
              <th className="p-4 w-24" />
            </tr>
          </thead>
          <tbody>
            {admins.map((u) => (
              <tr key={u.id} className="border-b border-theme-secondary/10 hover:bg-white/[0.02]">
                <td className="p-4 text-white flex items-center gap-2">
                  <UserCog size={16} className="text-[#E63946]" />
                  {u.name || "—"}
                </td>
                <td className="p-4 text-white/60 font-mono text-xs">{u.username || "—"}</td>
                <td className="p-4 text-white/60">{u.email}</td>
                <td className="p-4 text-white/60">{u.branch_name || "—"}</td>
                <td className="p-4">
                  <button type="button" onClick={() => del(u.id)} className="text-red-400/70 hover:text-red-400">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {!admins.length && (
              <tr>
                <td colSpan={5} className="p-8 text-center text-white/40">
                  No hay administradores de sucursal.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="glass w-full max-w-md p-6 rounded-sm space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-white">Nuevo administrador</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <label className="block space-y-1">
              <span className="text-xs text-white/60">Sucursal</span>
              <select
                className="input-dark w-full"
                value={form.branch_id}
                onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
              >
                <option value="" disabled>
                  Selecciona…
                </option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-white/60">Nombre</span>
              <input
                className="input-dark w-full"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoComplete="off"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-white/60">Usuario</span>
              <input
                className="input-dark w-full font-mono text-sm"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
                autoComplete="off"
              />
              <p className="text-[11px] text-white/40 mt-1">
                Para iniciar sesión en /admin/login (mín. 3 caracteres).
              </p>
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-white/60">Correo</span>
              <input
                type="email"
                className="input-dark w-full"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                autoComplete="off"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-white/60">Contraseña</span>
              <input
                type="password"
                className="input-dark w-full"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                autoComplete="new-password"
              />
              <p className="text-[11px] text-white/40 mt-1">
                Mínimo 8 caracteres, con mayúscula, minúscula y número.
              </p>
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-white/60">Confirmar contraseña</span>
              <input
                type="password"
                className="input-dark w-full"
                value={form.password_confirm}
                onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                autoComplete="new-password"
              />
            </label>
            <button type="button" onClick={save} className="btn-primary w-full justify-center">
              Crear usuario
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
